// src/utils/checkinCompleteness.ts
// ---------------------------------------------------------------------------
// v6.478 — ΕΝΑΣ κανόνας πληρότητας για ΟΛΗ τη ροή check-in / check-out.
//
// ΓΙΑΤΙ ΥΠΑΡΧΕΙ:
// Το vessel-final-page.tsx έκανε validate τη σελίδα 4 με hardcoded key list
// ('items','navItems','safetyItems',...) που ΔΕΝ υπάρχει στο page4_checklist —
// εκεί το μόνο array είναι `technicalCheckItems`. Άρα και τα 54 ελλιπή items
// περνούσαν ΑΟΡΑΤΑ και ο υπάλληλος έβλεπε μόνο κόκκινο πλαίσιο 3 δευτ.
// Εδώ το σκανάρισμα είναι ΓΕΝΙΚΟ: πιάνει ΚΑΘΕ array από item rows, όποιο κι αν
// είναι το όνομά του, τώρα και στο μέλλον.
//
// ΙΔΙΟΣ κανόνας με api/pending-checkins.php (pc_scan).
// ---------------------------------------------------------------------------

export type Mode = 'in' | 'out';

export interface SectionCount {
  key: string;
  total: number;
  done: number;
  missing: number;
  /** Το πρώτο ελλιπές item (για να λέμε ΤΙ ακριβώς λείπει). */
  firstMissingKey: string | null;
}

export interface PageCount {
  page: 'page2' | 'page3' | 'page4';
  total: number;
  done: number;
  missing: number;
  sections: SectionCount[];
}

/** Προαιρετικός εξοπλισμός: μετριέται, αλλά ΔΕΝ μπλοκάρει το submit. */
export const NON_BLOCKING_KEYS = ['optionalItems'];

// ---------------------------------------------------------------------------
// ΚΑΝΟΝΕΣ ΑΝΑ ITEM (master_items) — ΚΡΙΣΙΜΟ ΓΙΑ ΝΑ ΜΗΝ ΜΠΛΟΚΑΡΕΙ ΣΩΣΤΗ ΔΟΥΛΕΙΑ.
//
// Το αποθηκευμένο checklist_data ΔΙΑΤΗΡΕΙ items που στο μεταξύ απενεργοποιήθηκαν
// (active=false) ή που ισχύουν ΜΟΝΟ στο check-out (hide_checkin/checkout_only).
// Μετρημένο στη βάση: 236 ανενεργά + 75 κρυμμένα-για-το-mode αποτυπώματα σε 92
// γραμμές page4. Χωρίς φίλτρο, ο ναύλος 195 (σωστά υποβεβλημένος) έβγαζε
// «λείπουν 4» (ergatis, hatch_large, hatch_toilet, hatch_cabin) — ΨΕΥΔΩΣ.
//
// Πηγή αλήθειας: master-items.php (επιστρέφει ΜΟΝΟ active + τα hide flags).
// ---------------------------------------------------------------------------
export interface ItemRules {
  /** Κλειδιά που ΜΕΤΡΑΝΕ σε αυτό το mode. */
  allowed: Set<string>;
  /** false = δεν φορτώθηκαν κανόνες → ΔΕΝ μπλοκάρουμε (fail-open). */
  loaded: boolean;
}

const truthy = (v: any) => v === true || v === 't' || v === 1 || v === '1';

/**
 * Κλειδιά ενεργά ΓΙΑ ΑΥΤΟ ΤΟ ΣΚΑΦΟΣ, από yacht-items.php (χωρίς include_inactive).
 * ΚΡΙΣΙΜΟ: τα items ΔΙΑΦΕΡΟΥΝ ανά σκάφος (Maria 1: 127 ενεργά, Bar Bar: 145) και
 * το master_items ΔΕΝ το ξέρει αυτό. Μετρημένο: 160 αποθηκευμένα items είναι
 * ανενεργά στο συγκεκριμένο σκάφος. Χωρίς αυτό το φίλτρο, οι ναύλοι 722/346/705
 * (Maria 1, σωστά υποβεβλημένοι) έβγαζαν ψευδώς «λείπει».
 */
export function vesselKeySet(yachtItems: any[]): Set<string> | null {
  if (!Array.isArray(yachtItems) || yachtItems.length === 0) return null;
  const s = new Set<string>();
  for (const yi of yachtItems) {
    if (yi && yi.item_key && yi.active !== false && yi.active !== 'f') s.add(yi.item_key);
  }
  return s.size > 0 ? s : null;
}

/**
 * Χτίζει τους κανόνες: master_items (page_group) ∩ yacht_items (σκάφος).
 * Αν λείπει ΟΠΟΙΟΔΗΠΟΤΕ από τα δύο → loaded=false → ΔΕΝ μπλοκάρουμε (fail-open).
 */
export function buildRules(masterItems: any[], mode: Mode, vesselKeys?: Set<string> | null): ItemRules {
  const allowed = new Set<string>();
  if (!Array.isArray(masterItems) || masterItems.length === 0) {
    return { allowed, loaded: false };
  }
  for (const mi of masterItems) {
    if (!mi || !mi.item_key) continue;
    // Το API έχει ήδη κόψει τα master active=false. Εδώ κόβουμε τα κρυμμένα για ΑΥΤΟ το mode.
    const hidden = mode === 'out' ? truthy(mi.hide_checkout) : truthy(mi.hide_checkin);
    if (hidden) continue;
    // ...και όσα δεν είναι ενεργά ΣΕ ΑΥΤΟ ΤΟ ΣΚΑΦΟΣ.
    if (vesselKeys && !vesselKeys.has(mi.item_key)) continue;
    allowed.add(mi.item_key);
  }
  // vesselKeys υποχρεωτικό: χωρίς αυτό δεν ξέρουμε τι έχει ΑΥΤΟ το σκάφος.
  return { allowed, loaded: allowed.size > 0 && !!vesselKeys };
}

/** Μετράει αυτό το item στο τρέχον mode; */
function countsForMode(it: any, rules?: ItemRules): boolean {
  if (!rules || !rules.loaded) return true;   // fail-open: ποτέ ψεύτικο μπλοκάρισμα
  const k = it?.key || it?.item_key;
  if (!k) return true;
  return rules.allowed.has(k);
}

const isItemRow = (o: any): boolean =>
  !!o && typeof o === 'object' && !Array.isArray(o) &&
  ('inOk' in o || 'out' in o);

/** Ίδιος κανόνας με το υπάρχον isIncomplete του page5 και με τον server. */
export const isItemDone = (it: any, mode: Mode): boolean =>
  mode === 'in' ? it?.inOk === true : (it?.out === 'ok' || it?.out === 'not');

const labelOf = (it: any): string =>
  it?.name || it?.nameEn || it?.key || it?.item_key || it?.id || '?';

/**
 * Σκανάρει ένα checklist_data object και επιστρέφει counts ανά section.
 * Παραλείπει τα skipKeys (default: optionalItems).
 */
export function scanSections(
  data: any,
  mode: Mode,
  skipKeys: string[] = NON_BLOCKING_KEYS,
  prefix = '',
  rules?: ItemRules
): SectionCount[] {
  const out: SectionCount[] = [];
  if (!data || typeof data !== 'object') return out;

  for (const [key, val] of Object.entries(data as Record<string, any>)) {
    if (skipKeys.includes(key)) continue;
    if (!val || typeof val !== 'object') continue;

    // nested map of arrays (π.χ. dynamicSectionData)
    if (!Array.isArray(val)) {
      out.push(...scanSections(val, mode, skipKeys, prefix ? `${prefix}.${key}` : key, rules));
      continue;
    }

    if (!val.length || !isItemRow(val[0])) continue;

    let total = 0, done = 0;
    let firstMissingKey: string | null = null;
    for (const it of val) {
      if (!isItemRow(it)) continue;
      if (!countsForMode(it, rules)) continue;   // ανενεργό ή άλλου mode → ΔΕΝ μετράει
      total++;
      if (isItemDone(it, mode)) done++;
      else if (!firstMissingKey) firstMissingKey = labelOf(it);
    }
    if (total > 0) {
      out.push({
        key: prefix ? `${prefix}.${key}` : key,
        total, done, missing: total - done, firstMissingKey,
      });
    }
  }
  return out;
}

/** Συγκεντρωτικά για μία σελίδα. */
export function countPage(
  page: 'page2' | 'page3' | 'page4',
  data: any,
  mode: Mode,
  rules?: ItemRules,
  skipKeys: string[] = NON_BLOCKING_KEYS
): PageCount {
  const sections = scanSections(data, mode, skipKeys, '', rules);
  const total = sections.reduce((s, x) => s + x.total, 0);
  const done = sections.reduce((s, x) => s + x.done, 0);
  return { page, total, done, missing: total - done, sections };
}

/** Ανθρώπινο όνομα section για τα μηνύματα. */
const SECTION_LABELS: Record<string, { el: string; en: string }> = {
  items:            { el: 'Εξοπλισμός',              en: 'Equipment' },
  navItems:         { el: 'Όργανα Ναυσιπλοΐας',      en: 'Navigation' },
  safetyItems:      { el: 'Σωστικά',                 en: 'Safety' },
  genItems:         { el: 'Γεννήτρια / Γενικά',      en: 'General' },
  deckItems:        { el: 'Κατάστρωμα',              en: 'Deck' },
  fdeckItems:       { el: 'Πλώρη',                   en: 'Fore Deck' },
  dinghyItems:      { el: 'Βοηθητικό (Dinghy)',      en: 'Dinghy' },
  fendersItems:     { el: 'Μπαλόνια',                en: 'Fenders' },
  boathookItems:    { el: 'Γάντζος',                 en: 'Boathook' },
  cabin:            { el: 'Καμπίνες',                en: 'Cabins' },
  hullItems:        { el: 'Γάστρα',                  en: 'Hull' },
  optionalItems:    { el: 'Προαιρετικός Εξοπλισμός', en: 'Optional Equipment' },
  technicalCheckItems: { el: 'Τεχνικός Έλεγχος',     en: 'Technical Check' },
};

export function sectionLabel(key: string, lang: string): string {
  const base = key.split('.').pop() || key;
  const l = SECTION_LABELS[base];
  if (!l) return base;
  return lang === 'el' ? l.el : l.en;
}

export const PAGE_LABELS: Record<string, { el: string; en: string }> = {
  page2: { el: 'Σελίδα 2', en: 'Page 2' },
  page3: { el: 'Σελίδα 3', en: 'Page 3' },
  page4: { el: 'Σελίδα 4', en: 'Page 4' },
  page5: { el: 'Σελίδα 5', en: 'Page 5' },
};

export function pageLabel(page: string, lang: string): string {
  const l = PAGE_LABELS[page];
  if (!l) return page;
  return lang === 'el' ? l.el : l.en;
}
